import { renterInfoProblems } from '../listing/goodToKnow.js'

/**
 * The mid-term settings a property has, and Guesty does not.
 *
 * THE BOUNDARY, WHICH IS THE WHOLE DESIGN
 *
 * Stated by the Product Owner: *"我们不能改任何东西,因为我们是从 Guesty 抓的,我们不改任何东西"*.
 * Guesty is the source of truth for what a home IS — address, rooms, photographs, amenities,
 * calendar. This file holds only what Guesty has no field for, and every entry below exists
 * because something in this product is currently running on an invented number.
 *
 *   · MONTHLY RENT — Guesty has none. It has a nightly rate and a monthly factor and multiplies
 *     them, and the factor is not a policy: 0.75 on 35 listings, 0.80 on 60, 0.85 on 3, 0.90 on
 *     27, and 1.00 on one home that therefore charges a month at thirty nights.
 *   · THE FLOOR — P-02 needs one per home. Today it is 90% of the derived figure, a number this
 *     project invented and nobody agreed. It is the only thing standing between an agent and a
 *     rent that loses money.
 *   · THE ANNUAL RATE — P-08 requires one for twelve months or longer and Guesty holds none, so
 *     **all 40 homes currently refuse a year-long lease**: the largest deal this business writes
 *     cannot be quoted, because there is nowhere to put the number.
 *   · THE DEPOSIT — Guesty's is a short-stay deposit. Median $500 against a median monthly rent
 *     of $5,603 — nine per cent of a month, on a tenancy P-07 says should be a month.
 *   · CLEANING — 125 of 125 listings hold it as PER_STAY. Monthly and fortnightly cleaning exist
 *     only here, because they are how this business sells, not how Guesty models them.
 *   · WHETHER THE OWNER DOES MID-TERM AT ALL — no field anywhere, so it is the one setting on
 *     this page that decides whether a home exists at all for this business. It is a switch, and
 *     switching it off takes the home out of the quote search and off the calendar. Until it did
 *     that, an agent's memory was the only thing stopping a quote going out on a property whose
 *     owner had said no.
 */

/**
 * Cleaning, ordered by how often it happens.
 *
 * The order is what makes an owner's minimum enforceable: an owner who insists on a clean every
 * month has ruled out "once at checkout", and the screen must stop that being offered rather than
 * rely on an agent remembering.
 */
export const CLEANING_FREQUENCY_ORDER = ['CHECKOUT', 'MONTHLY', 'BIWEEKLY', 'WEEKLY']

/**
 * Each frequency carries its OWN price per clean, and the reason is the Product Owner's:
 * *"这三种的价格肯定会不一样,你扫得越频繁,通常你的价格就会越低"*. A fortnightly clean is
 * lighter work than a full turnover, so it costs less per visit. One price for all of them would
 * have quoted a weekly clean at turnover rates and lost every renter who wanted one.
 */
export const CLEANING_FREQUENCIES = [
  { id: 'CHECKOUT', label: 'Once, at checkout' },
  { id: 'MONTHLY', label: 'Monthly' },
  { id: 'BIWEEKLY', label: 'Every two weeks' },
  { id: 'WEEKLY', label: 'Weekly' },
]

export const OWNER_CLEANING_MINIMUM = {
  NONE: 'NONE',
  MONTHLY: 'MONTHLY',
  BIWEEKLY: 'BIWEEKLY',
  WEEKLY: 'WEEKLY',
}

export const OWNER_CLEANING_MINIMUM_LABEL = {
  NONE: 'No minimum',
  MONTHLY: 'At least monthly',
  BIWEEKLY: 'At least every two weeks',
  WEEKLY: 'At least weekly',
}

/** Is `schedule` frequent enough to satisfy `minimum`? */
export function meetsCleaningMinimum(schedule, minimum) {
  if (!minimum || minimum === OWNER_CLEANING_MINIMUM.NONE) return true
  return CLEANING_FREQUENCY_ORDER.indexOf(schedule) >= CLEANING_FREQUENCY_ORDER.indexOf(minimum)
}

/**
 * CLEANING PRICES AS A TABLE: ONE PRICE, THREE DISCOUNTS (MTR #76).
 *
 * The Product Owner, 2026-09-26: the one-time clean is the full price, and a home that is cleaned
 * regularly pays less per visit — monthly 90%, every two weeks 85%, weekly 80% of it. "这样子我们
 * 就可以解决了每一个 property 要全部重新去输入的这一个难题." One number per home, not four.
 *
 * HOW IT IS STORED, WITHOUT A NEW COLUMN. `cleaningPrices` keeps what a person typed:
 *   · only CHECKOUT (or nothing)  → the home uses the company defaults, and the other three are
 *     worked out from it every time they are read — change the default, every such home follows;
 *   · all four                    → somebody switched the defaults off and set this home's own.
 * So "uses the defaults" is not a flag that can disagree with the prices: it IS the absence of
 * the other three.
 *
 * AND WHEN CHECKOUT IS EMPTY, GUESTY'S CLEANING FEE IS THE BASE (Product Owner, 2026-09-26: "可以
 * 先抓 Guesty 的清洁费"). It is Guesty's figure for a short stay, used until somebody types one.
 */
export const CLEANING_DEFAULT_DISCOUNTS = Object.freeze({ MONTHLY: 10, BIWEEKLY: 15, WEEKLY: 20 })
export const DISCOUNTED_FREQUENCIES = Object.freeze(['MONTHLY', 'BIWEEKLY', 'WEEKLY'])

const positiveOrNull = (v) => (v != null && v !== '' && Number.isFinite(Number(v)) && Number(v) > 0 ? Number(v) : null)

/**
 * The company's discounts as stored (MTR #79, `mtr.company_settings.cleaning_discounts`), made
 * safe to price with: each a whole percentage from 0 to 90, and anything missing or nonsense
 * falls back to the figure the product shipped with — never to 0, which would silently make
 * a regular clean cost as much as a one-time one, and never above 90, which would make it free.
 */
export function normalizeDiscounts(value) {
  const out = {}
  for (const id of DISCOUNTED_FREQUENCIES) {
    const v = Number(value?.[id])
    out[id] = Number.isInteger(v) && v >= 0 && v <= 90 ? v : CLEANING_DEFAULT_DISCOUNTS[id]
  }
  out.offered = normalizeOffered(value?.offered)
  return out
}

/**
 * WHICH CLEANING THE COMPANY OFFERS AT ALL (Product Owner, 2026-10-18).
 *
 * 「我们其实目前所有的做法都是只提供一次打扫，在 check out 的时候。然后如果要提供 monthly 或是
 * weekly 的 cleaning，我们先要跟打扫的人聊好细节…之后我们聊好业务细节了，我们再把这个选项重新打开」.
 *
 * So it is a setting beside the discounts, in the same row (`mtr.company_settings.cleaning_discounts`,
 * key `offered`), not a line of code: switching Monthly back on is a tick and a Save on Pricing ›
 * Cleaning. A row written before this has no `offered`, and means what the product did then — all
 * four — so nothing changes until somebody untick one. An empty or unreadable list is never "offer
 * no cleaning" (a quote with no cleaning line is refused); it falls back to all four as well.
 */
const CLEANING_OFFERED_DEFAULT = Object.freeze([...CLEANING_FREQUENCY_ORDER])
function normalizeOffered(value) {
  if (!Array.isArray(value)) return [...CLEANING_OFFERED_DEFAULT]
  const picked = CLEANING_FREQUENCY_ORDER.filter((id) => value.includes(id))
  return picked.length ? picked : [...CLEANING_OFFERED_DEFAULT]
}

/** Tick or untick one frequency; the last one ticked cannot be unticked. Kept in the frequencies' order. */
export function toggleOffered(offered, id) {
  const next = offered.includes(id) ? offered.filter((x) => x !== id) : [...offered, id]
  return next.length ? CLEANING_FREQUENCY_ORDER.filter((x) => next.includes(x)) : offered
}

/** How many homes keep a regular clean the company is not offering, because their owner requires it. */
export function homesOwnerKeeps(settingsByUnit = {}, offered = CLEANING_OFFERED_DEFAULT) {
  return Object.values(settingsByUnit).filter((s) => s?.cleaningOwnerMinimum && s.cleaningOwnerMinimum !== 'NONE'
    && !offered.some((id) => meetsCleaningMinimum(id, s.cleaningOwnerMinimum))).length
}

/**
 * The frequencies one home offers, given its prices, its owner's minimum and what the company
 * offers. The owner's minimum is a contract and wins: a home whose owner insists on a monthly clean
 * keeps the least frequent clean that satisfies it even when the company has switched monthly off,
 * because the other answer is a home nobody can quote at all. `ownerOverride` says when that happened.
 */
function offeredForHome({ prices = {}, ownerMinimum = null, offered = CLEANING_OFFERED_DEFAULT }) {
  const allowed = CLEANING_FREQUENCY_ORDER.filter((id) => prices[id] != null && meetsCleaningMinimum(id, ownerMinimum))
  const kept = allowed.filter((id) => offered.includes(id))
  if (kept.length || !allowed.length) return { schedules: kept, ownerOverride: false }
  return { schedules: [allowed[0]], ownerOverride: true }
}

/** How many set-up homes follow the company discounts, and how many set their own (MTR #79). */
/** What the ? beside the cleaning warning says: the homes that set their own prices and are not affected; '' when none do. */
export function ownPricesNote(own) {
  if (!(own > 0)) return ''
  return `${own} ${own === 1 ? 'home sets its' : 'homes set their'} own prices and ${own === 1 ? 'is' : 'are'} not affected.`
}

export function discountReach(settingsByUnit = {}) {
  let following = 0, own = 0
  for (const s of Object.values(settingsByUnit)) {
    if (!s) continue
    if (usesDefaultDiscounts(s)) following += 1
    else own += 1
  }
  return { following, own }
}

/** Whole dollars, `off` percent off `base`. */
export const discountedPrice = (base, off) => Math.round(base * (100 - off) / 100)

/** True when none of the three discounted frequencies has a price of its own. */
export function usesDefaultDiscounts(settings) {
  const p = settings?.cleaningPrices ?? {}
  return DISCOUNTED_FREQUENCIES.every((id) => p[id] == null)
}

/**
 * The four rows as the screen and every price read them.
 * `{ base, baseSource: 'set'|'guesty'|null, useDefault, rows: { [id]: { price, off } } }`.
 */
export function cleaningTable(settings, guestyFee = null, discounts = CLEANING_DEFAULT_DISCOUNTS) {
  const set = settings?.cleaningPrices ?? {}
  const own = set.CHECKOUT != null ? Number(set.CHECKOUT) : null
  const guesty = positiveOrNull(guestyFee)
  const base = own ?? guesty
  const baseSource = own != null ? 'set' : guesty != null ? 'guesty' : null
  const useDefault = usesDefaultDiscounts(settings)
  const rows = { CHECKOUT: { price: base, off: 0 } }
  for (const id of DISCOUNTED_FREQUENCIES) {
    if (useDefault) {
      rows[id] = { price: base == null ? null : discountedPrice(base, discounts[id]), off: discounts[id] }
    } else {
      const v = set[id] == null ? null : Number(set[id])
      rows[id] = { price: v, off: v != null && base > 0 ? Math.round((1 - v / base) * 1000) / 10 : null }
    }
  }
  return { base, baseSource, useDefault, rows }
}

/**
 * Settings as every price, quote and listing reads them: the four cleaning prices filled in, and
 * the offered schedules recomputed from them. `cleaningResolvedFrom` remembers what was typed, so
 * `unresolveCleaning` can put back exactly that when these settings are saved again.
 */
export function withResolvedCleaning(settings, guestyFee = null, discounts = CLEANING_DEFAULT_DISCOUNTS) {
  if (!settings) return settings
  if (settings.cleaningResolvedFrom) return settings
  const t = cleaningTable(settings, guestyFee, discounts)
  const all = {}
  for (const id of CLEANING_FREQUENCY_ORDER) if (t.rows[id]?.price != null) all[id] = t.rows[id].price
  /*
    A FREQUENCY THE COMPANY DOES NOT OFFER HAS NO PRICE ON ANY SCREEN (2026-10-18). Every quote, the
    Listing and the renter's page read the prices worked out here, and a frequency with no price is
    never offered — so leaving it out here is the one place the setting has to act. What a person
    typed is untouched (`cleaningResolvedFrom`), so switching it back on brings the same price back.
  */
  const offered = normalizeOffered(discounts?.offered)
  const { schedules, ownerOverride } = offeredForHome({ prices: all, ownerMinimum: settings.cleaningOwnerMinimum, offered })
  const prices = Object.fromEntries(Object.entries(all).filter(([id]) => offered.includes(id) || schedules.includes(id)))
  return {
    ...settings,
    cleaningPrices: prices,
    cleaningSchedules: schedules,
    cleaningOffered: [...new Set([...offered, ...schedules])],
    cleaningOwnerOverride: ownerOverride,
    cleaningResolvedFrom: settings.cleaningPrices ?? {},
  }
}

/**
 * Back from what was read to what should be stored. A figure that is still exactly what the rules
 * worked out is dropped (so a home stays on the defaults, and on Guesty's fee); a figure somebody
 * changed is kept. If that change gave a discounted frequency a price of its own, the home has left
 * the defaults, and the other two are written down as they stood rather than silently lost.
 */
export function unresolveCleaning(settings, guestyFee = null, discounts = CLEANING_DEFAULT_DISCOUNTS) {
  if (!settings?.cleaningResolvedFrom) return settings
  /* What was worked out on reading — the company's offer and the owner's override — is not stored. */
  // eslint-disable-next-line no-unused-vars
  const { cleaningResolvedFrom: raw, cleaningOffered: _o, cleaningOwnerOverride: _x, ...rest } = settings
  const worked = withResolvedCleaning({ ...rest, cleaningPrices: raw }, guestyFee, discounts).cleaningPrices
  const out = { ...raw }
  for (const [k, v] of Object.entries(settings.cleaningPrices ?? {})) {
    if (v !== worked[k]) out[k] = v
  }
  if (usesDefaultDiscounts({ cleaningPrices: raw }) && !usesDefaultDiscounts({ cleaningPrices: out })) {
    for (const id of DISCOUNTED_FREQUENCIES) if (out[id] == null && worked[id] != null) out[id] = worked[id]
  }
  return {
    ...rest,
    cleaningPrices: out,
    /*
      THE SCHEDULES COUNT GUESTY'S FEE TOO (review of #343). Worked out from the typed prices alone,
      a home that runs on Guesty's cleaning fee — every typed price empty — came back with no
      schedule at all. Nothing read that (schedules are recomputed on every read), until `validate`
      started guarding the quote screen's save and refused it: "At least one cleaning schedule must
      be offered", on a home that offers one.
    */
    cleaningSchedules: withResolvedCleaning({ ...rest, cleaningPrices: out }, guestyFee, discounts).cleaningSchedules,
  }
}

/**
 * The three answers an owner actually gives about pets.
 *
 * From the Product Owner, 2026-09-04. The third is the one a two-state field would have destroyed:
 * *"default 拒绝,但是如果有特别好的情况、特别好的 rate 可以接受弹性的预定"*. Stored as "no" it
 * loses a real deal; stored as "yes" it promises something the owner never agreed. It is the same
 * shape as a booking-window block — a condition, not a refusal — and it has to be its own value.
 */
export const PET_POLICY = {
  NEVER: 'NEVER',
  WITH_FEE: 'WITH_FEE',
  CASE_BY_CASE: 'CASE_BY_CASE',
}

export const PET_POLICY_LABEL = {
  NEVER: 'No pets',
  WITH_FEE: 'With fee',
  CASE_BY_CASE: 'Ask owner',
}

export const PET_POLICY_NOTE = {
  NEVER: 'A renter with a pet will not be offered this home, and the search will say why.',
  WITH_FEE: 'Quoted automatically with the fee below. Nobody needs asking.',
  CASE_BY_CASE: 'The default answer is no. An agent may ask when the rate or the stay justifies it — the quote carries the condition until the owner answers.',
}

/**
 * How the deposit scales with the length of the stay.
 *
 * Also from the Product Owner: a year is one month's rent, six months is half a month, and
 * anything shorter is a flat figure. P-07's "one month unless overridden" turns out to be the
 * TOP of a ladder rather than the whole rule — and a $500 Guesty deposit on a $5,600 home was the
 * bottom of no ladder at all.
 */
export const DEPOSIT_BANDS = [
  { id: 'yearDollars', label: '12 months or more', test: (m) => m >= 12 },
  { id: 'midDollars', label: '6 to 12 months', test: (m) => m >= 6 },
  { id: 'shortDollars', label: 'Under 6 months', test: () => true },
]

/**
 * What each band starts at, so nobody types three numbers from nothing.
 *
 * A year is one month's rent; six-to-twelve is half a month. Both are suggestions printed into an
 * editable box, not rules — the Product Owner asked for figures he can change, and a rule that
 * quietly computes is a rule nobody checks.
 */
export function seedDeposits(monthlyRent) {
  if (monthlyRent == null) return { yearDollars: null, midDollars: null, shortDollars: null }
  return {
    yearDollars: monthlyRent,
    midDollars: Math.round(monthlyRent / 2),
    shortDollars: null,
  }
}

export function depositFor(settings, months) {
  const d = settings?.depositLadder
  if (!d) return null
  const band = DEPOSIT_BANDS.find((b) => b.test(months))
  const amount = d[band.id]
  /* Integer cents whatever was typed (#334): `1024.09 * 100` is 102408.99999999999. */
  return amount == null ? null : { cents: Math.round(amount * 100), band: band.id, label: band.label }
}

/**
 * UTILITIES — the second number on every mid-term quote, and until now there was nowhere to put it.
 *
 * WHY THIS IS A PROPERTY SETTING AND NOT A QUOTE FIELD
 *
 * What water, power, gas and the rest cost is a fact about the HOME — its size, its heating, its
 * garden, whether the building bills trash centrally. It is the same number in March as it is for
 * the renter who asks in June, and typing it fresh onto every quote is how two agents quote the
 * same house at $150 and $260 in the same week.
 *
 * WHETHER IT IS INCLUDED IS A QUOTE DECISION, NOT A PROPERTY ONE. The Product Owner was asked
 * whether a lease of a year or more should be treated differently — the market convention is that
 * a long tenant puts the accounts in their own name — and his answer was no:
 *
 *     "长短都一样，只是每张报价自己选。"
 *
 * This file's first version still kept a per-home DEFAULT for which shape a quote opens in. He cut
 * that too — "how is this house usually quoted? 这段也去掉吧" — and he is right, for a reason worth
 * writing down: a default on the home is a quiet answer to a question the agent is about to be
 * asked anyway, two screens earlier, by somebody who does not know which renter is asking. It
 * saves one click and hides one decision. The home holds the MONEY and the LIST. Which shape this
 * particular quote takes is decided on the quote, in front of the person deciding it.
 *
 * ── TWO NUMBERS, AND THEY MEAN DIFFERENT THINGS ────────────────────────────────────────────────
 *
 * `monthly` is an ESTIMATE. It is what this home's utilities usually run to, and it is what
 * appears on a quote that bills them on top: "$200 a month, on top of the rent". If January comes
 * in at $340 the renter pays $340, because they are paying the bill.
 *
 * `cap` is a PROMISE. It only appears on a quote that includes utilities in the rent, and it
 * is the ceiling on what this business absorbs. Above it, the renter pays the difference.
 *
 * The cap exists because of the one thing this business cannot control. A quote that says
 * "utilities included" with no ceiling is an open-ended liability on the single cost a renter can
 * run up at will — and the Product Owner's reason for wanting it is better than the accounting
 * one:
 *
 *     "我也可以让他知道说超过这个数字的租客自己会负责…让他们知道要省电省水，以免他们进去以后
 *      水电从来不关。"
 *
 * A cap printed on the renter's own copy is not primarily a recovery mechanism. It is the sentence
 * that makes somebody close a window in February.
 */
export const UTILITIES = [
  { id: 'WATER', label: 'Water', short: 'water' },
  /* Billed with water in most of this portfolio, and frequently the larger half of that bill. */
  { id: 'SEWER', label: 'Sewer', short: 'sewer' },
  { id: 'ELECTRICITY', label: 'Electricity', short: 'electricity' },
  { id: 'GAS', label: 'Gas', short: 'gas' },
  { id: 'TRASH', label: 'Trash & recycling', short: 'trash' },
  { id: 'INTERNET', label: 'Internet / WiFi', short: 'WiFi' },
  { id: 'LANDSCAPING', label: 'Landscaping', short: 'landscaping' },
]

export const utilityLabel = (id) => UTILITIES.find((u) => u.id === id)?.label ?? id

/**
 * The covered list as a sentence a renter reads: "water, electricity, gas and trash".
 *
 * The short forms exist for exactly this. "Trash & recycling and internet / WiFi" is what a tick
 * list reads like when it is read out as prose, and a renter's quote is prose.
 */
export function utilitiesSentence(covers = []) {
  const names = (covers ?? [])
    .map((id) => UTILITIES.find((u) => u.id === id)?.short ?? String(id).toLowerCase())
  if (names.length === 0) return ''
  if (names.length === 1) return names[0]
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

export const UTILITY_BASIS = {
  /** The rent covers them, up to the cap. */
  INCLUDED: 'INCLUDED',
  /** Quoted as a separate monthly figure beside the rent. */
  ON_TOP: 'ON_TOP',
}

/* The two answers as Build Quote names them (2026-10-19). */
export const UTILITY_BASIS_LABEL = {
  INCLUDED: 'Included in rent',
  ON_TOP: 'Billed on top',
}

export const UTILITY_BASIS_NOTE = {
  INCLUDED: 'The renter pays one number. Anything above the monthly cap is theirs, and the quote says so.',
  ON_TOP: 'The renter sees the rent and the utilities as two lines, and a monthly total underneath.',
}

/**
 * What the cap starts at, so nobody types a second number from nothing.
 *
 * A quarter above the typical month, rounded to the nearest ten dollars. It is a suggestion in an
 * editable box, exactly like `seedDeposits` — a number this software computed is never allowed to
 * masquerade as one the business agreed.
 */
export function seedUtilityCap(monthly) {
  if (monthly == null) return null
  return Math.round((monthly * 1.25) / 10) * 10
}

/**
 * The utilities block for a quote, or a refusal saying what is missing.
 *
 * Returns `null` when this home has no utilities figure at all — which is not an error and not a
 * zero. It means nobody has said what this home costs to run, and a quote that silently treated
 * that as "$0, included" would be promising something for nothing.
 */
export function hasUtilities(settings) {
  return settings?.utilities?.monthly != null
}

/** Can this home be quoted with utilities included? Only with a ceiling on what we absorb. */
export function canIncludeUtilities(settings) {
  return hasUtilities(settings) && settings.utilities.cap != null
}

/**
 * IS THIS HOME OFFERED FOR MID-TERM? — and the default is the whole of it.
 *
 * Only an explicit `false` takes a home out. A home with no settings row has not been answered
 * about, and most of the portfolio is in exactly that state: reading absence as "no" would empty
 * the calendar and the search in one go, which is the single way this rule can be catastrophically
 * wrong. So absence keeps the behaviour this product had before the switch existed — the home is
 * offered — and nothing disappears until somebody says it should.
 *
 * This does not contradict "absence is never a verdict". The verdict here is not being INFERRED
 * from the missing value; the missing value is being left alone, and the screen still reports an
 * unconfigured home as unconfigured. What is refused is the other direction: silently hiding a
 * home nobody has ruled on.
 */
export function isOfferedForMidTerm(settings) {
  return settings?.offeredForMidTerm !== false
}

/**
 * WHY THIS OUTLIVES THE QUESTION THAT PRODUCED IT.
 *
 * "Ask the owner each time / Pre-approved" was a row on Property setup and is not any more. The
 * Product Owner removed the question itself, not the answer: *"是不是每次都要询问业主，这一行可以
 * 剃掉。我们不需要每次都询问业主"* — the business does not ask owners one stay at a time, so a
 * screen that made an agent answer for 204 homes was asking about a process that does not exist.
 *
 * The COLUMN stays. `mtr.property_settings.owner_approval` is `not null` with a checked set of
 * values, and it holds answers real people gave on this screen before today. Dropping it in the
 * same breath as removing the question would destroy that record to tidy up a screen, and the two
 * decisions are not the same size. So this constant remains, `seedSettings` keeps writing a valid
 * value into it, and nothing reads it to price or to filter anything — verified: the only other
 * `needsOwnerConfirmation` in this product comes from the booking-window verdict in
 * availability/search.js and has never been connected to this field.
 */
export const OWNER_APPROVAL = {
  PRE_APPROVED: 'PRE_APPROVED',
  ASK_EVERY_TIME: 'ASK_EVERY_TIME',
}

/**
 * A home with no settings is NOT a home with default settings.
 *
 * It is a home nobody has set up, and the difference decides whether it may be quoted. Returning
 * plausible defaults here would make an unconfigured property indistinguishable from a configured
 * one — the same defect as an empty calendar day reading as "unavailable".
 */
export function isConfigured(settings) {
  return !!settings && settings.configuredAt != null
}

/** What the screen offers as a starting point — labelled as seeded, never as agreed. */
export function seedSettings(unit, { at }) {
  const p = unit.prices ?? {}
  const derived =
    typeof p.basePrice === 'number' && typeof p.monthlyPriceFactor === 'number'
      ? Math.round(p.basePrice * 30 * p.monthlyPriceFactor)
      : null
  return {
    configuredAt: null,
    offeredForMidTerm: true,
    /* No longer asked on any screen; still written so the not-null column never sees undefined. */
    ownerApproval: OWNER_APPROVAL.ASK_EVERY_TIME,
    monthlyRent: derived,
    monthlyRentSource: derived == null ? null : 'DERIVED_FROM_GUESTY',
    floor: derived == null ? null : Math.round(derived * 0.9),
    floorSource: derived == null ? null : 'SEEDED_AT_90_PERCENT',
    annualRent: null,
    deposit: null, // null means P-07's default: one month's rent
    cleaningOwnerMinimum: OWNER_CLEANING_MINIMUM.NONE,
    cleaningSchedules: ['CHECKOUT'],
    /** One price per frequency, keyed by frequency id. */
    /* Whole dollars, like every money box on this page (#334–#336): Guesty allows cents, we do not. */
    cleaningPrices: { CHECKOUT: typeof p.cleaningFee === 'number' ? Math.round(p.cleaningFee) : null,
      MONTHLY: null, BIWEEKLY: null, WEEKLY: null },
    pricingStrategyId: null,
    pets: {
      policy: PET_POLICY.CASE_BY_CASE,
      /**
       * Per animal, not a combined total.
       * *"有的人是不是限制一只狗、两只猫之类的,而不是猫跟狗混合起来"* — "two pets" cannot
       * express "one dog or two cats", and an owner who said the second would be quoted the first.
       */
      limits: { DOG: null, CAT: null },
      maxWeightLb: null,
      feeAmount: typeof p.petFee === 'number' && p.petFee > 0 ? Math.round(p.petFee) : null,
      feeBasis: 'PER_STAY',
      refundableDeposit: null,
      conditions: '',
    },
    /**
     * Three bands, all editable, all named by their length rather than by a rule.
     * "≥ 12 months / 6–12 months / < 6 months" — the Product Owner's wording, and it is better
     * than the prose it replaced because a band a person can read is a band they can check.
     */
    depositLadder: { yearDollars: null, midDollars: null, shortDollars: null },
    /**
     * Utilities. Empty rather than guessed: what a home costs to run is not derivable from
     * anything Guesty holds, and a seeded figure here would be a number on a renter's quote that
     * nobody in this business ever agreed to.
     */
    utilities: {
      /* Dollars, like `monthlyRent` and `floor` beside it. The database column is cents. */
      monthly: null,
      cap: null,
      covers: [],
    },
    notes: '',
    /* What a renter reads under "Good to know" on the home's page (src/listing/goodToKnow.js). Empty until somebody answers. */
    renterInfo: {},
    seededAt: at,
  }
}

/**
 * What is missing before this home can be quoted properly.
 *
 * Returned as a list rather than a boolean, because "not ready" is not an answer anybody can act
 * on. Each entry names the consequence, not the field.
 */
export function gaps(settings, { annualNeeded = true } = {}) {
  const out = []
  if (!settings) return [{ id: 'ALL', severity: 'high', says: 'Nothing has been set up for mid-term on this home.' }]
  if (!isOfferedForMidTerm(settings)) {
    return [{ id: 'NOT_OFFERED', severity: 'info', says: 'The mid-term listing is switched off. It is hidden from Listing and will not appear in a search, or on the calendar.' }]
  }
  if (settings.monthlyRent == null) {
    out.push({ id: 'NO_RENT', severity: 'high', says: 'No monthly rent, so this home cannot be quoted at all.' })
  } else if (settings.monthlyRentSource === 'DERIVED_FROM_GUESTY' && settings.configuredAt == null) {
    out.push({
      id: 'RENT_DERIVED',
      severity: 'medium',
      says: 'The rent is still the figure derived from Guesty’s nightly rate — nobody has agreed it as a monthly rent.',
    })
  }
  if (settings.floor == null) {
    out.push({ id: 'NO_FLOOR', severity: 'high', says: 'No floor, so nothing stops an agent quoting below cost.' })
  } else if (settings.floorSource === 'SEEDED_AT_90_PERCENT' && settings.configuredAt == null) {
    out.push({ id: 'FLOOR_SEEDED', severity: 'medium', says: 'The floor is a seeded 90% of the rent. That number came from this software, not from the business.' })
  }
  if (annualNeeded && settings.annualRent == null) {
    out.push({
      id: 'NO_ANNUAL',
      severity: 'high',
      says: 'No annual rate, so a lease of twelve months or longer will be refused — the largest kind of deal this business writes.',
    })
  }
  for (const f of settings.cleaningSchedules ?? []) {
    if (settings.cleaningPrices?.[f] == null) {
      out.push({ id: 'NO_CLEAN_PRICE_' + f, severity: 'medium',
        says: `“${CLEANING_FREQUENCIES.find((x) => x.id === f)?.label ?? f}” is offered with no price, so it cannot appear on a quote.` })
    }
  }
  for (const b of DEPOSIT_BANDS) {
    if (settings.depositLadder?.[b.id] == null) {
      out.push({
        id: 'NO_DEPOSIT_' + b.id,
        severity: b.id === 'shortDollars' ? 'medium' : 'low',
        says: `No deposit set for “${b.label}”.`,
      })
    }
  }
  /*
    A home with no utilities figure can still be quoted — the agent simply has nothing to put on
    the second line. It is medium rather than high for that reason: the quote goes out, it is just
    silent about a cost the renter will certainly ask about.
  */
  if (settings.utilities?.monthly == null) {
    out.push({
      id: 'NO_UTILITIES',
      severity: 'medium',
      says: 'No utilities figure, so a quote cannot say what water, power and the rest cost — or offer to include them.',
    })
  } else if ((settings.utilities?.covers ?? []).length === 0) {
    out.push({
      id: 'NO_UTILITIES_COVERS',
      severity: 'medium',
      says: 'There is a utilities figure but nothing ticked, so the quote would name a price for something it cannot describe.',
    })
  } else if (settings.utilities?.cap == null) {
    out.push({
      id: 'NO_UTILITY_CAP',
      severity: 'low',
      says: 'No utilities cap, so this home cannot be quoted with utilities included — only billed on top.',
    })
  }
  if (settings.pricingStrategyId == null) {
    out.push({
      id: 'NO_MODEL',
      severity: 'medium',
      says: 'No pricing strategy, so this home is priced flat — no month adjustment, no length rule, no lead time.',
    })
  }
  if (settings.pets?.policy === 'WITH_FEE' && settings.pets?.feeAmount == null) {
    out.push({ id: 'NO_PET_FEE', severity: 'medium', says: 'Pets are accepted with a fee, and no fee has been set.' })
  }
  return out
}

/** Refuses a configuration that would produce a quote nobody can honour. */
/*
  WHOLE DOLLARS, IN EVERY MONEY BOX (#334, #335; the Product Owner, 2026-10-02: "全部只收整数元").

  The minimum was the first box to be held to this (#315, below). The others stored cents and read
  them back rounded, so a rent typed as 3450.50 was priced at $3,451 and nobody could see the
  stored figure (#335), and a deposit typed as 1024.09 printed as $1,024.8.999999999985448 on a
  quote (#334). Every box on this page now refuses what the minimum refuses, in the same order:
  the sign, then the size, then the cents — and names the box, because "an amount is wrong"
  sends somebody hunting through eleven of them.

  THE TEST IS THE SAVE'S OWN ARITHMETIC (`Math.round(x * 100)`, `toCents` in src/data/map.js), so
  2155.000000001 — which the save stores as a legal 215500 — passes, exactly as it does for the
  minimum. `aboveZero` is for the two rents, where $0 is not a price; a deposit or a fee of $0 is
  a real answer ("none") and is allowed.
*/
function wholeDollarProblem(label, value, { aboveZero = false } = {}) {
  if (value == null) return null
  const n = Number(value)
  if (!Number.isFinite(n) || n < 0 || (aboveZero && n === 0)) {
    return aboveZero
      ? `${label} must be an amount above $0 — leave it empty if nobody has set one yet.`
      : `${label} cannot be below $0.`
  }
  const c = Math.round(n * 100)
  if (!Number.isSafeInteger(c)) return `${label} is larger than any amount this product can hold — check the figure.`
  if (c % 100 !== 0) return `${label}: whole dollars only — $${Math.round(n).toLocaleString('en-US')}, not $${n.toLocaleString('en-US', { minimumFractionDigits: 2 })}.`
  return null
}

const cleaningLabel = (f) => `“${CLEANING_FREQUENCIES.find((x) => x.id === f)?.label ?? f}” cleaning`

function moneyProblems(settings) {
  const boxes = [
    ['Monthly rent', settings.monthlyRent, { aboveZero: true }],
    ['Annual rate', settings.annualRent, { aboveZero: true }],
    ['Deposit', settings.deposit],
    ...Object.entries(settings.cleaningPrices ?? {}).map(([f, v]) => [cleaningLabel(f), v]),
    ['Pet fee', settings.pets?.feeAmount],
    ['Extra deposit for the pet', settings.pets?.refundableDeposit],
    ['Utilities, usually costs', settings.utilities?.monthly],
    ['Utilities, included up to', settings.utilities?.cap],
    ...DEPOSIT_BANDS.map((b) => [`Deposit for ${b.label.charAt(0).toLowerCase()}${b.label.slice(1)}`, settings.depositLadder?.[b.id]]),
  ]
  return boxes.map(([label, v, o]) => wholeDollarProblem(label, v, o)).filter(Boolean)
}

export function validate(settings) {
  const errors = [...moneyProblems(settings), ...renterInfoProblems(settings)]
  if (settings.monthlyRent != null && settings.floor != null && settings.floor > settings.monthlyRent) {
    errors.push('The floor is above the monthly rent, so every quote on this home would be refused.')
  }
  /*
    THE MINIMUM IS WHOLE DOLLARS (#315), and since migration 0048 the database says so with a check
    constraint. Without this the save travels all the way to postgres and is refused there. (Until
    #336 the screen also said "Saved" next to a number the database never took; `saveHomeSettings`
    now waits for the answer, but refusing here first still says which box, in words.)

    THE TEST IS `toCents` ITSELF (`Math.round(x * 100)`, src/data/map.js), which is the arithmetic the
    save performs, so this refuses exactly what the write would refuse and nothing more. `x % 1` would
    be STRICTER than the save: it rejects 2155.000000001, which `toCents` rounds to a perfectly legal
    215500. A guard that refuses a save the database would have accepted is its own defect.

    AND IT CHECKS THE SIGN FIRST, which matters more than the cents. The box is `<input type="number"
    min="0">` and React enforces neither attribute, so -2155 arrives here — whole dollars, and postgres
    agrees (`-215500 % 100` is 0), so the constraint would take it and P-02 would never refuse a quote
    on this home again. `''`, `0` and `[]` all reach `Number()` as 0 and would store a $0 minimum,
    which is the same thing said more quietly. `Number.isSafeInteger` is the upper end: 1e20 dollars
    is past what a bigint column holds, and that failure reads like nothing at all.
  */
  if (settings.floor != null) {
    const floor = Number(settings.floor)
    if (!Number.isFinite(floor) || floor <= 0) {
      errors.push('The minimum must be an amount of money above $0 — leave it empty if nobody has set one yet.')
    } else if (!Number.isSafeInteger(Math.round(floor * 100))) {
      errors.push('That minimum is larger than any rent this product can hold — check the figure.')
    } else if (Math.round(floor * 100) % 100 !== 0) {
      errors.push('The minimum must be whole dollars — $2,155, not $2,154.60.')
    }
  }
  if (settings.annualRent != null && settings.monthlyRent != null && settings.annualRent > settings.monthlyRent * 12) {
    errors.push('The annual rate is higher than twelve months at the monthly rent. A year would cost more than paying by the month, which no renter will accept.')
  }
  for (const [f, price] of Object.entries(settings.cleaningPrices ?? {})) {
    if (price != null && !(Number(price) > 0)) {
      errors.push(`The “${CLEANING_FREQUENCIES.find((x) => x.id === f)?.label ?? f}” cleaning price must be above $0 — leave it empty if it is not offered.`)
    }
  }
  if (settings.cleaningSchedules?.length === 0) errors.push('At least one cleaning schedule must be offered, or a quote has no cleaning line at all.')

  // An owner's minimum is not advice. Offering something less frequent than it would put a
  // promise on a quote that the owner has already refused.
  for (const sched of settings.cleaningSchedules ?? []) {
    if (!meetsCleaningMinimum(sched, settings.cleaningOwnerMinimum)) {
      errors.push(`This owner requires cleaning ${OWNER_CLEANING_MINIMUM_LABEL[settings.cleaningOwnerMinimum].toLowerCase()}, so “${sched}” cannot be offered.`)
    }
  }

  /*
    A cap at or below the typical month is not a cap, it is a surcharge with a friendly name: the
    renter would be billed extra in an ordinary month, having been told utilities were included.
  */
  const u = settings.utilities
  if (u?.cap != null && u?.monthly != null && u.cap < u.monthly) {
    errors.push('The utilities cap is below what this home usually costs to run, so a renter told '
      + '"utilities included" would be billed extra in a normal month.')
  }
  if (u?.monthly != null && !(u.monthly > 0)) {
    errors.push('A utilities figure of zero is not "free" — leave it empty if nobody has worked it out yet.')
  }

  const pets = settings.pets
  if (pets?.policy === PET_POLICY.WITH_FEE && (pets.animals ?? []).length === 0) {
    errors.push('Pets are accepted, but no animal is ticked — a renter with a dog would be told yes and then no.')
  }
  if (pets?.maxWeightLb != null && !(pets.maxWeightLb > 0)) errors.push('A weight limit must be a real weight.')
  return errors
}

/**
 * A person typed a minimum rent. It is stored as `floor` — the one name `settingsToRow` persists.
 *
 * The editor used to write `minimum`, a name nothing saves, while showing `minimum ?? floor`: the
 * box displayed the saved floor, accepted a new one, and the save quietly kept the old. Found by
 * review on #50, where Listing readiness began to depend on the saved floor.
 */
export const withFloor = (settings, floor) => ({ ...settings, floor, floorSource: 'AGREED' })
